import React, { useCallback, useEffect, useState } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import Modal from "../common/Modal.jsx";
import AdminService from "../../services/adminService.js";

export const FORCE_DISCONNECT_MESSAGES = {
  success: "Tapo account force disconnected successfully.",
  providerFailed:
    "Tapo API disconnect failed, but the SmartZman connection and linked data were successfully cleaned up.",
  nothingToClean:
    "No Tapo connection or linked data was found for this user. Nothing needed cleaning.",
  fallbackError: "Force disconnect failed. Please try again.",
};

/**
 * What to tell the admin about a 2xx answer. A Tapo failure with a completed
 * local cleanup is NOT an error — the SmartZman side is clean, which is the
 * whole point of forcing it — so it is reported as info, not red.
 */
export function forceDisconnectOutcome(data) {
  const d = data?.data || {};
  const counts = ` Removed ${d.devicesRemoved ?? 0} device(s); updated ${d.schedulesUpdated ?? 0} schedule(s).`;

  if (data?.localCleanup === "nothing_to_clean") {
    return { kind: "info", text: FORCE_DISCONNECT_MESSAGES.nothingToClean };
  }
  if (data?.providerDisconnect === "failed") {
    return { kind: "info", text: FORCE_DISCONNECT_MESSAGES.providerFailed + counts };
  }
  return { kind: "success", text: FORCE_DISCONNECT_MESSAGES.success + counts };
}

/** The backend's own sentence, whichever field this route family puts it in. */
export function forceDisconnectErrorText(err) {
  const body = err?.response?.data;
  const text =
    (typeof body?.message === "string" && body.message) ||
    (typeof body?.error === "string" && body.error) ||
    (Array.isArray(body?.errors) && body.errors.filter((e) => typeof e === "string").join(", ")) ||
    (!err?.response && typeof err?.message === "string" && err.message) ||
    FORCE_DISCONNECT_MESSAGES.fallbackError;
  return text.slice(0, 300);
}

export default function ForceDisconnectDialog({ target, onCancel, onDone }) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  // A fresh dialog for every target.
  useEffect(() => {
    setPending(false);
    setError(null);
  }, [target]);

  const open = Boolean(target);
  // Stable identity: Modal re-runs its focus/scroll-lock effect whenever
  // onClose changes, which would bounce focus on every re-render.
  const close = useCallback(() => {
    if (!pending) onCancel?.();
  }, [pending, onCancel]);

  const confirm = async () => {
    if (!target || pending) return;
    setPending(true);
    setError(null);
    try {
      const data = await AdminService.forceDisconnectTapo(target.userId, target.accountId);
      setPending(false);
      onDone?.(data);
    } catch (err) {
      setPending(false);
      setError(forceDisconnectErrorText(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      title="Force Disconnect Tapo Account?"
      subtitle={target ? `Tapo account ${target.accountId}` : undefined}
      footer={
        <>
          <button
            type="button"
            onClick={close}
            disabled={pending}
            className="px-4 py-2 text-[11px] font-black uppercase tracking-widest border border-slate-200 rounded-md bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={pending}
            className="px-4 py-2 text-[11px] font-black uppercase tracking-widest rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60 transition-colors"
          >
            {pending ? "Disconnecting…" : "Force Disconnect"}
          </button>
        </>
      }
    >
      {target && (
        <div className="space-y-4 text-sm text-slate-700">
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
            <FaExclamationTriangle className="mt-0.5 shrink-0" />
            <p className="text-[12px] font-semibold">
              This is an administrative action and cannot be automatically undone.
            </p>
          </div>

          <div className="rounded border border-slate-200 bg-slate-50 p-3">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Target account</p>
            <p className="font-bold text-slate-800">{target.name || "—"}</p>
            <p className="text-[12px] text-slate-500">{target.email || "—"}</p>
          </div>

          <p>This will attempt to disconnect the Tapo account first.</p>
          <p>
            If the Tapo API is unavailable, the SmartZman provider connection and this user's linked
            Tapo devices will still be removed, and those devices will be taken out of their
            schedules. A schedule left with no devices is moved to draft.
          </p>
          <p className="text-[12px] text-slate-500">
            Only this user's data is affected. Other devices, schedules and accounts are not touched.
          </p>

          {error && (
            <div role="alert" className="rounded border border-rose-200 bg-rose-50 p-3 text-[12px] font-semibold text-rose-700">
              {error}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
